// Zod DTOs for the P2-25 command channel (poll-based backend -> frontend
// control bus, docs/design/P2-25-command-channel-contract.md §4 "Command
// types v1 — exact payload schemas"). Discriminated on `type`. Backend
// validates on enqueue (reject) and on read (drop poison, §3.1); frontend
// validates on receive (drop + ack + log; never throw out of the poll loop).
import { z } from 'zod';
import { CommandType } from '../enums';

// ── FORCE_LOGOUT ──────────────────────────────────────────────────────────
export const forceLogoutReasonSchema = z.enum([
  'SESSION_SUPERSEDED', // a newer login took over this account
  'ADMIN_REVOKED', // an admin signed this user out
  'ACCOUNT_SUSPENDED', // status left ACTIVE mid-session
  'PASSWORD_CHANGED', // credentials rotated elsewhere
]);
export const forceLogoutPayloadSchema = z
  .object({
    reason: forceLogoutReasonSchema,
    message: z.string().max(200).optional(), // rendered as TEXT ONLY, never v-html
  })
  .strict();

// ── JOB_STATUS ────────────────────────────────────────────────────────────
export const jobStatusPayloadSchema = z
  .object({
    jobId: z.string().min(1).max(64),
    job: z.string().min(1).max(64), // e.g. 'section.duplicate', 'students.import'
    status: z.enum(['QUEUED', 'RUNNING', 'SUCCEEDED', 'FAILED']),
    progress: z.number().int().min(0).max(100).optional(),
    message: z.string().max(200).optional(),
    resultId: z.string().max(64).optional(),
  })
  .strict();

// ── RELOAD ────────────────────────────────────────────────────────────────
export const reloadPayloadSchema = z
  .object({
    scope: z.enum(['APP', 'CONFIG', 'ENTITLEMENTS']).default('APP'),
  })
  .strict();

// ── DATA_CHANGED ──────────────────────────────────────────────────────────
export const dataChangedEntitySchema = z.enum([
  'EXAM_BOARD',
  'LEVEL',
  'SUBJECT',
  'SECTION',
  'RESOURCE_TAB',
  'RESOURCE',
  'ASSET',
  'ENROLLMENT',
  'SUBSCRIPTION',
  'USER',
  // T4-T5 addition (topbar-technical-approach.md §7/§8): T6 (a follow-up
  // task, different worker) enqueues `DATA_CHANGED { entity: 'NOTIFICATION',
  // id: null, op: 'CREATED' }` after `NotificationService.emit` so the
  // client's existing command-poll channel invalidates the notification
  // queries — see `../../apps/api/src/services/notifications.ts`'s header.
  'NOTIFICATION',
]);
export const dataChangedPayloadSchema = z
  .object({
    entity: dataChangedEntitySchema,
    id: z.string().min(1).max(64).nullable(), // null = "the whole collection changed"
    op: z.enum(['CREATED', 'UPDATED', 'DELETED']).optional(),
  })
  .strict();

// ── Envelope ──────────────────────────────────────────────────────────────
export const userCommandSchema = z.discriminatedUnion('type', [
  z.object({
    id: z.string().min(1).max(64),
    createdAt: z.string().datetime(),
    type: z.literal(CommandType.FORCE_LOGOUT),
    payload: forceLogoutPayloadSchema,
  }),
  z.object({
    id: z.string().min(1).max(64),
    createdAt: z.string().datetime(),
    type: z.literal(CommandType.JOB_STATUS),
    payload: jobStatusPayloadSchema,
  }),
  z.object({
    id: z.string().min(1).max(64),
    createdAt: z.string().datetime(),
    type: z.literal(CommandType.RELOAD),
    payload: reloadPayloadSchema,
  }),
  z.object({
    id: z.string().min(1).max(64),
    createdAt: z.string().datetime(),
    type: z.literal(CommandType.DATA_CHANGED),
    payload: dataChangedPayloadSchema,
  }),
]);
export type UserCommand = z.infer<typeof userCommandSchema>;

export const commandPollResponseSchema = z.object({
  commands: z.array(userCommandSchema).max(50),
  hasMore: z.boolean(),
  nextPollMs: z.number().int().min(5000).max(300000),
});
export type CommandPollResponse = z.infer<typeof commandPollResponseSchema>;

export const commandAckRequestSchema = z.object({
  ids: z
    .array(
      z
        .string()
        .min(1)
        .max(64)
        .regex(/^[A-Za-z0-9_-]{1,64}$/),
    )
    .min(1)
    .max(50),
});
export type CommandAckRequest = z.infer<typeof commandAckRequestSchema>;
